import { Plus, Trash2, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react'

import { newId } from './blocks'
import { AlignPicker } from './blockEditors'
import type { TableBlock, TableColumn, TableColumnAlign } from './types'

interface Props {
  block: TableBlock
  onChange: (next: TableBlock) => void
}

// The schema's row is one string per column, in column order — the only
// shape the renderers read (`row[ci]`). Some agent-authored pages saved rows
// as OBJECTS keyed by column id instead; the published table shows those as
// empty cells and array operations on them throw. Every row is read through
// this, so the editor shows what such a row means, and any edit writes the
// whole table back in the array shape (keys matching no column are dropped —
// they were never rendered anyway).
export function toArrayRow(row: unknown, columns: TableColumn[]): string[] {
  if (Array.isArray(row)) return row.map(v => (v == null ? '' : String(v)))
  if (row && typeof row === 'object') {
    const rec = row as Record<string, unknown>
    return columns.map(c => {
      const v = rec[c.id] ?? rec[c.label]
      return v == null ? '' : String(v)
    })
  }
  return columns.map(() => '')
}

export function TableBlockEditor({ block: saved, onChange }: Props) {
  const legacyRows = (saved.rows || []).some(r => !Array.isArray(r))
  const columnsList = saved.columns || []
  const block: TableBlock = {
    ...saved,
    columns: columnsList,
    rows: (saved.rows || []).map(r => toArrayRow(r, columnsList)),
  }
  const set = (patch: Partial<TableBlock>) => onChange({ ...block, ...patch })

  // ── column ops ──
  const addColumn = () => {
    const col: TableColumn = { id: newId('col'), label: `Column ${block.columns.length + 1}`, align: 'left' }
    const columns = [...block.columns, col]
    const rows = block.rows.map(r => [...r, ''])
    set({ columns, rows })
  }
  const removeColumn = (idx: number) => {
    const columns = block.columns.filter((_, i) => i !== idx)
    const rows = block.rows.map(r => r.filter((_, i) => i !== idx))
    set({ columns, rows })
  }
  const moveColumn = (idx: number, delta: -1 | 1) => {
    const to = idx + delta
    if (to < 0 || to >= block.columns.length) return
    const columns = [...block.columns]; ;[columns[idx], columns[to]] = [columns[to], columns[idx]]
    const rows = block.rows.map(r => {
      const next = [...r]; ;[next[idx], next[to]] = [next[to], next[idx]]
      return next
    })
    set({ columns, rows })
  }
  const updateColumn = (idx: number, patch: Partial<TableColumn>) => {
    const columns = block.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    set({ columns })
  }

  // ── row ops ──
  const addRow = () => set({ rows: [...block.rows, block.columns.map(() => '')] })
  const insertRowAt = (idx: number) => {
    const fresh = block.columns.map(() => '')
    const rows = [...block.rows.slice(0, idx), fresh, ...block.rows.slice(idx)]
    set({ rows })
  }
  const removeRow = (idx: number) => set({ rows: block.rows.filter((_, i) => i !== idx) })
  const moveRow = (idx: number, delta: -1 | 1) => {
    const to = idx + delta
    if (to < 0 || to >= block.rows.length) return
    const rows = [...block.rows]; ;[rows[idx], rows[to]] = [rows[to], rows[idx]]
    set({ rows })
  }
  const updateCell = (ri: number, ci: number, value: string) => {
    const rows = block.rows.map((r, i) => {
      if (i !== ri) return r
      const next = [...r]
      while (next.length < block.columns.length) next.push('')
      next[ci] = value
      return next
    })
    set({ rows })
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Field label="Heading">
        <input type="text" value={block.heading ?? ''} onChange={e => set({ heading: e.target.value })} style={inputStyle} />
      </Field>
      <Field label="Subheading">
        <input type="text" value={block.subheading ?? ''} onChange={e => set({ subheading: e.target.value })} style={inputStyle} />
      </Field>
      <Field label="Caption (small text below the table)">
        <input type="text" value={block.caption ?? ''} onChange={e => set({ caption: e.target.value })} style={inputStyle} />
      </Field>

      {legacyRows && (
        <div style={{ ...emptyStyle, textAlign: 'left', color: 'var(--color-warning, #d97706)' }}>
          These rows are saved as objects keyed by column id, which the published table
          cannot read (its cells show empty). Any edit in this panel rewrites them in
          the standard one-value-per-column form.{' '}
          <button type="button" onClick={() => set({})} style={addBtn}>Convert now</button>
        </div>
      )}

      <Field label="Alignment on the page">
        <AlignPicker value={block.align ?? 'left'} onChange={align => set({ align })} />
      </Field>

      {/* Style toggles */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <label style={chk}><input type="checkbox" checked={block.striped ?? false} onChange={e => set({ striped: e.target.checked })} /> Striped rows</label>
        <label style={chk}><input type="checkbox" checked={block.bordered ?? false} onChange={e => set({ bordered: e.target.checked })} /> Bordered</label>
        <label style={chk}><input type="checkbox" checked={block.compact ?? false} onChange={e => set({ compact: e.target.checked })} /> Compact</label>
      </div>

      {/* Columns */}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 8 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 13 }}>Columns ({block.columns.length})</strong>
          <button type="button" onClick={addColumn} style={addBtn}><Plus size={12} /> Add column</button>
        </header>
        {block.columns.map((c, idx) => (
          <div key={c.id} style={cardStyle}>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                type="text"
                value={c.label}
                onChange={e => updateColumn(idx, { label: e.target.value })}
                placeholder="Column name"
                style={{ ...inputStyle, flex: 1, fontWeight: 500 }}
              />
              <select
                value={c.align ?? 'left'}
                onChange={e => updateColumn(idx, { align: e.target.value as TableColumnAlign })}
                style={{ ...inputStyle, width: 90 }}
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
              <button type="button" onClick={() => moveColumn(idx, -1)} disabled={idx === 0} style={iconBtn} title="Move left"><ArrowLeft size={12} /></button>
              <button type="button" onClick={() => moveColumn(idx, 1)}  disabled={idx === block.columns.length - 1} style={iconBtn} title="Move right"><ArrowRight size={12} /></button>
              <button type="button" onClick={() => removeColumn(idx)} disabled={block.columns.length <= 1} style={{ ...iconBtn, color: '#dc2626' }} title="Remove"><Trash2 size={12} /></button>
            </div>
            <input
              type="text"
              value={c.width ?? ''}
              onChange={e => updateColumn(idx, { width: e.target.value || undefined })}
              placeholder="Width (e.g. 120px or 20%)"
              style={{ ...inputStyle, fontSize: 13 }}
            />
          </div>
        ))}
      </div>

      {/* Rows */}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 8 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 13 }}>Rows ({block.rows.length})</strong>
          <button type="button" onClick={addRow} style={addBtn}><Plus size={12} /> Add row</button>
        </header>
        {block.rows.length === 0 && (
          <div style={emptyStyle}>No rows yet — click <em>Add row</em>.</div>
        )}
        {block.rows.map((row, ri) => (
          <div key={ri} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>Row {ri + 1}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" onClick={() => insertRowAt(ri)} style={iconBtn} title="Insert row above"><Plus size={12} /><ArrowUp size={10} /></button>
                <button type="button" onClick={() => insertRowAt(ri + 1)} style={iconBtn} title="Insert row below"><Plus size={12} /><ArrowDown size={10} /></button>
                <button type="button" onClick={() => moveRow(ri, -1)} disabled={ri === 0} style={iconBtn} title="Move up"><ArrowUp size={12} /></button>
                <button type="button" onClick={() => moveRow(ri, 1)}  disabled={ri === block.rows.length - 1} style={iconBtn} title="Move down"><ArrowDown size={12} /></button>
                <button type="button" onClick={() => removeRow(ri)} style={{ ...iconBtn, color: '#dc2626' }} title="Remove row"><Trash2 size={12} /></button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${block.columns.length}, 1fr)`, gap: 4 }}>
              {block.columns.map((c, ci) => (
                <input
                  key={c.id}
                  type="text"
                  value={row[ci] ?? ''}
                  onChange={e => updateCell(ri, ci, e.target.value)}
                  placeholder={c.label}
                  style={{ ...inputStyle, fontSize: 13 }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontFamily: 'inherit',
}
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 6,
  padding: 8, display: 'grid', gap: 6, background: 'var(--color-bg-secondary)',
}
const iconBtn: React.CSSProperties = {
  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 4, padding: 4, cursor: 'pointer', color: 'var(--color-text-secondary)',
  display: 'inline-flex', alignItems: 'center',
}
const addBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg-secondary)', cursor: 'pointer',
}
const chk: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
}
const emptyStyle: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary)',
  padding: 12, textAlign: 'center', background: 'var(--color-bg-secondary)',
  borderRadius: 6, border: '1px dashed var(--color-border)',
}
